import { resolveCindroBaseUrl } from '@cindro/client';
import type {
  CindroApp,
  CindroOrganization,
  CindroReleaseApp,
  CindroReleaseAppVersion,
  CindroSessionUser,
  CindroTeam,
  CreateAppDto,
  CreateCustomEnvironmentDto,
  CreateEnvironmentDto,
  CreateReleaseAppDto,
  UpdateReleaseAppDto,
  CreateReleaseAppVersionDto,
  UpdateReleaseAppVersionDto,
  CreateOrganizationDto,
  CreateReleaseChannelDto,
  CreateTeamDto,
  CreateTenantDomainDto,
  CustomEnvironmentResponse,
  PromoteReleaseDto,
  RollbackReleaseDto,
  UpdateReleaseChannelDto,
  ReleaseAppNativeIdentityResponse,
  SignupDto,
  TenantDomainResponse,
  UpsertReleaseAppNativeIdentityDto,
  CindroOrgMember,
  CindroOrgInvite
} from '@cindro/contracts';
import axios from 'axios';
import {
  clearSessionToken,
  emitSessionChange,
  getSessionToken,
  recoverSessionFromRefreshToken,
  setSessionTokens
} from './api-session';
import type { CindroSession } from './api-session';
export {
  clearSessionToken,
  getRefreshToken,
  getSessionToken,
  setRefreshToken,
  setSessionToken,
  setSessionTokens,
  subscribeSessionChanges
} from './api-session';
export type { CindroSession } from './api-session';
import type {
  CreateDeliveryCampaignInput,
  CreateReleaseShipmentInput,
  DeliveryCandidateRow,
  DeliveryCampaignRow,
  PipelineRunRow,
  ReleaseArtifactRow,
  ReleaseChannelRow,
  ReleasePromotionRow,
  ReleaseShipmentRow,
  StageRunRow
} from './api-delivery-types';
import type {
  AgentRunnerRow,
  AgentRunnerJobRow,
  AgentProfileRow,
  AgentWorkPlanRow,
  DispatchRunRow,
  PipelineDefinitionRow,
  WorkJourneyRow
} from './api-pipeline-types';
export type {
  AgentRunnerRow,
  AgentRunnerJobRow,
  AgentProfileRow,
  AgentWorkPlanRow,
  DispatchRunRow,
  PipelineDefinitionRow,
  PipelineDefinitionStage,
  WorkJourneyAttentionRow,
  WorkJourneyExpectationRow,
  WorkJourneyItemRow,
  WorkJourneyRow,
  WorkPolicySnapshotRow,
  WorkResponsibilityRow
} from './api-pipeline-types';
export type {
  CreateDeliveryCampaignInput,
  CreateReleaseShipmentInput,
  DeliveryCampaignGateRow,
  DeliveryCampaignRow,
  DeliveryCampaignTargetRow,
  DeliveryCandidateArtifactRow,
  DeliveryCandidateRow,
  PipelineRunRow,
  ReleaseArtifactRow,
  ReleaseChannelRow,
  ReleasePromotionRow,
  ReleaseShipmentRow,
  ReleaseShipmentTargetRow,
  StageRunRow
} from './api-delivery-types';

const cindroServerBaseURL = resolveCindroBaseUrl(
  process.env.NX_PUBLIC_CINDRO_SERVER_URL || window.location.origin
);

// The first screen cannot recover while an identity or workspace request is
// allowed to wait forever. Keep this timeout local to bootstrap reads so long
// running uploads and operator actions retain their own request semantics.
const BOOTSTRAP_REQUEST_TIMEOUT_MS = 15_000;

export function getCindroApiClient(token?: string) {
  const client = axios.create({
    baseURL: `${cindroServerBaseURL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined
  });

  // Attach a unique idempotency key to every mutating request so the server
  // can safely replay the response if the same request is retried.
  //
  // Only generate a key when one isn't already present on the request config.
  // This interceptor runs again when the 401-refresh-retry below re-dispatches
  // `originalRequest` through this same client — if we always overwrote the
  // header, the retry would get a *different* key than the original attempt,
  // so the server's idempotency cache (keyed on that header) would never
  // recognize it as a retry and would process it as a brand new mutation.
  // That silently defeated the dedup this header exists for, and was the
  // root cause of resources sometimes being created 2-3x on a single submit.
  client.interceptors.request.use((config) => {
    const method = (config.method || '').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      config.headers = config.headers ?? {};
      if (!config.headers['X-Idempotency-Key']) {
        config.headers['X-Idempotency-Key'] = crypto.randomUUID();
      }
    }
    return config;
  });

  if (token) {
    client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const originalRequest = error?.config;

        if (
          status !== 401 ||
          !originalRequest ||
          originalRequest._retry === true
        ) {
          throw error;
        }

        // A workspace load creates several short-lived API clients in
        // parallel. If one request has already rotated the token, late 401s
        // from the other clients must reuse that token instead of each
        // starting another refresh and remounting the workspace again.
        const currentAccessToken = getSessionToken();
        if (currentAccessToken && currentAccessToken !== token) {
          originalRequest._retry = true;
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${currentAccessToken}`
          };
          client.defaults.headers.common.Authorization = `Bearer ${currentAccessToken}`;
          return client(originalRequest);
        }

        const refreshedSession =
          await recoverSessionFromRefreshToken(refreshTenantSession);
        if (!refreshedSession) {
          emitSessionChange(null);
          clearSessionToken();
          throw error;
        }

        originalRequest._retry = true;
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${refreshedSession.accessToken}`
        };
        client.defaults.headers.common.Authorization = `Bearer ${refreshedSession.accessToken}`;

        return client(originalRequest);
      }
    );
  }

  return client;
}

export function getCindroServerBaseURL() {
  return cindroServerBaseURL;
}

export type EnvironmentRow = {
  tenantEnvironmentId: string;
  tenantOrganizationId: string;
  tenantProjectId?: string;
  slug: string;
  name: string;
  region?: string;
  isActive: boolean;
  created?: string;
  updated?: string;
};

export type CindroApiKeyRow = {
  tenantApiKeyId: string;
  name: string;
  scopes: string[];
  isActive: boolean;
  lastUsedAt?: string;
  expiresAt?: string;
  created: string;
  updated: string;
  prefix?: string;
};

export type TenantApiKeyRow = CindroApiKeyRow;

export type CindroProject = CindroApp;
export type CreateProjectDto = CreateAppDto;

export type ReleaseAppRow = CindroReleaseApp;
export type ReleaseAppVersionRow = CindroReleaseAppVersion;

export async function listReleaseApps(
  token: string,
  scope: { organizationId: string; projectId: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<ReleaseAppRow[]>('/releases/apps', {
    params: scope
  });
  return response.data;
}

export async function createReleaseApp(
  token: string,
  input: CreateReleaseAppDto
) {
  const client = getCindroApiClient(token);
  const response = await client.post<ReleaseAppRow>('/releases/apps', input);
  return response.data;
}

export async function updateReleaseApp(
  token: string,
  releaseAppId: string,
  input: UpdateReleaseAppDto
) {
  const client = getCindroApiClient(token);
  const response = await client.patch<ReleaseAppRow>(
    `/releases/apps/${releaseAppId}`,
    input
  );
  return response.data;
}

export async function listReleaseAppVersions(
  token: string,
  scope: { organizationId: string; projectId: string; releaseAppId: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<ReleaseAppVersionRow[]>(
    `/releases/apps/${scope.releaseAppId}/versions`,
    {
      params: {
        organizationId: scope.organizationId,
        projectId: scope.projectId
      }
    }
  );
  return response.data;
}

export async function createReleaseAppVersion(
  token: string,
  releaseAppId: string,
  input: CreateReleaseAppVersionDto
) {
  const client = getCindroApiClient(token);
  const response = await client.post<ReleaseAppVersionRow>(
    `/releases/apps/${releaseAppId}/versions`,
    input
  );
  return response.data;
}

export async function updateReleaseAppVersion(
  token: string,
  releaseAppId: string,
  releaseAppVersionId: string,
  input: UpdateReleaseAppVersionDto
) {
  const client = getCindroApiClient(token);
  const response = await client.patch<ReleaseAppVersionRow>(
    `/releases/apps/${releaseAppId}/versions/${releaseAppVersionId}`,
    input
  );
  return response.data;
}

export type TenantDomainRow = TenantDomainResponse;
export type ReleaseAppNativeIdentityRow = ReleaseAppNativeIdentityResponse;

export async function listTenantDomains(
  token: string,
  scope: { organizationId: string; projectId?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<TenantDomainRow[]>('/domains', {
    params: scope
  });
  return response.data;
}

export async function createTenantDomain(
  token: string,
  input: CreateTenantDomainDto
) {
  const client = getCindroApiClient(token);
  const response = await client.post<TenantDomainRow>('/domains', input);
  return response.data;
}

export async function verifyTenantDomain(
  token: string,
  tenantDomainId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.post<TenantDomainRow>(
    `/domains/${tenantDomainId}/verify`
  );
  return response.data;
}

export type CustomEnvironmentRow = CustomEnvironmentResponse;

export async function listCustomEnvironments(
  token: string,
  scope: { organizationId: string; projectId?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<CustomEnvironmentRow[]>(
    '/custom-environments',
    { params: scope }
  );
  return response.data;
}

export async function createCustomEnvironment(
  token: string,
  input: CreateCustomEnvironmentDto
) {
  const client = getCindroApiClient(token);
  const response = await client.post<CustomEnvironmentRow>(
    '/custom-environments',
    input
  );
  return response.data;
}

export async function deleteCustomEnvironment(
  token: string,
  environmentId: string,
  organizationId: string
) {
  const client = getCindroApiClient(token);
  await client.delete(`/custom-environments/${environmentId}`, {
    params: { organizationId }
  });
}

export async function getReleaseAppNativeIdentity(
  token: string,
  releaseAppId: string
) {
  const client = getCindroApiClient(token);
  try {
    const response = await client.get<ReleaseAppNativeIdentityRow>(
      `/releases/apps/${releaseAppId}/native-identity`
    );
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function upsertReleaseAppNativeIdentity(
  token: string,
  releaseAppId: string,
  input: UpsertReleaseAppNativeIdentityDto
) {
  const client = getCindroApiClient(token);
  const response = await client.put<ReleaseAppNativeIdentityRow>(
    `/releases/apps/${releaseAppId}/native-identity`,
    input
  );
  return response.data;
}

export type RuntimeObserverConnectionRow = {
  runtimeObserverConnectionId: string;
  organizationId: string;
  projectId: string;
  name: string;
  provider: 'prometheus';
  environment: string;
  namespace: string;
  status: 'active' | 'revoked';
  tokenPrefix: string;
  tokenExpiresAt: string;
  lastSeenAt?: string;
  created?: string;
  updated?: string;
};

export type RuntimeObservationRow = {
  runtimeObservationId: string;
  runtimeObserverConnectionId: string;
  organizationId: string;
  projectId: string;
  provider: 'prometheus';
  environment: string;
  namespace: string;
  workloadKind: 'deployment' | 'statefulset' | 'daemonset';
  workload: string;
  status: 'ready' | 'progressing' | 'degraded' | 'unknown';
  freshness: 'fresh' | 'stale';
  desiredReplicas: number;
  readyReplicas: number;
  availableReplicas: number;
  updatedReplicas: number;
  restarts?: number;
  image?: string;
  imageId?: string;
  containers?: Array<{
    name: string;
    image?: string;
    imageId?: string;
    images?: string[];
    imageIds?: string[];
    imageEvidenceComplete?: boolean;
    restarts: number;
  }>;
  observedAt: string;
  receivedAt?: string;
};

export type IssuedRuntimeObserverConnectionRow =
  RuntimeObserverConnectionRow & { observerToken: string };

export type ProjectRepoMappingRow = {
  projectRepoMappingId: string;
  organizationId: string;
  projectId?: string;
  projectIds: string[];
  provider: string;
  externalRepo: string;
  created?: string;
  updated?: string;
};

export type RuntimeConfigValueRow = {
  runtimeConfigValueId: string;
  key: string;
  value: string;
  valueType: 'string' | 'boolean' | 'number' | 'json';
  masked: boolean;
};

export type RuntimeConfigValueInput = {
  key: string;
  value: string;
  valueType?: 'string' | 'boolean' | 'number' | 'json';
  masked?: boolean;
};

export type RuntimeConfigRow = {
  runtimeConfigId: string;
  name: string;
  organizationId: string;
  projectId?: string;
  releaseAppVersionId?: string;
  tenantEnvironmentId?: string;
  nativeAppId?: string;
  hostname?: string;
  platform?: 'web' | 'android' | 'ios';
  nativeVersionRange?: string;
  environment: string;
  expressApiHostname: string;
  staticCdnPrefix?: string;
  otaChannelId?: string;
  otaChannel?: string;
  analyticsEnabled: boolean;
  analyticsPageviewsEnabled: boolean;
  posthogApiKey?: string;
  posthogHost?: string;
  otelEnabled: boolean;
  otelCollectorUrl?: string;
  priority: number;
  isActive: boolean;
  updatedByUserId?: string;
  created?: string;
  updated?: string;
  values?: RuntimeConfigValueRow[];
};

export type RuntimeConfigInput = {
  name: string;
  organizationId?: string;
  projectId?: string;
  releaseAppVersionId?: string | null;
  tenantEnvironmentId?: string;
  nativeAppId?: string | null;
  hostname?: string | null;
  platform?: 'web' | 'android' | 'ios';
  nativeVersionRange?: string | null;
  environment: string;
  expressApiHostname: string;
  staticCdnPrefix?: string | null;
  otaChannelId?: string | null;
  otaChannel?: string | null;
  analyticsEnabled?: boolean;
  analyticsPageviewsEnabled?: boolean;
  posthogApiKey?: string | null;
  posthogHost?: string | null;
  otelEnabled?: boolean;
  otelCollectorUrl?: string | null;
  priority?: number;
  isActive?: boolean;
};

export type ReleaseStorageConfigRow = {
  releaseStorageConfigId?: string;
  name: string;
  storageMode: 'managed' | 'self-managed' | 'dev-local';
  storageProvider: 's3' | 'r2' | 'custom';
  storageBucket: string;
  storageRegion: string;
  storageEndpoint?: string;
  storageCdnHost: string;
  storagePrefix: string;
  storageAccessKeyId?: string;
  storageSecretAccessKey?: string;
  hasSecretAccessKey: boolean;
  isActive: boolean;
  source: 'database' | 'environment';
  updatedByUserId?: string;
  created?: string;
  updated?: string;
};

async function refreshTenantSession(refreshToken: string) {
  const client = getCindroApiClient();
  const response = await client.post<CindroSession>('/auth/refresh', {
    refreshToken
  });
  return response.data;
}

export async function loginTenantUser(input: {
  email: string;
  password: string;
  organizationId?: string;
  organizationSlug?: string;
}): Promise<CindroSession> {
  const client = getCindroApiClient();
  const response = await client.post<CindroSession>('/auth/login', input);
  return response.data;
}

export async function signupTenantUser(
  input: SignupDto
): Promise<CindroSession> {
  const client = getCindroApiClient();
  const response = await client.post<CindroSession>('/auth/signup', input);
  return response.data;
}

export async function logoutTenantUser(token: string): Promise<void> {
  const client = getCindroApiClient(token);
  await client.post('/auth/logout');
}

export async function refreshTenantUserSession(refreshToken: string) {
  const session = await refreshTenantSession(refreshToken);
  setSessionTokens(session);
  emitSessionChange(session);
  return session;
}

export async function getCurrentTenantUser(token: string) {
  const client = getCindroApiClient(token);
  const response = await client.get<CindroSessionUser>('/auth/me', {
    timeout: BOOTSTRAP_REQUEST_TIMEOUT_MS
  });
  return response.data;
}

export async function listOrganizations(token: string) {
  const client = getCindroApiClient(token);
  const response = await client.get<CindroOrganization[]>('/organizations', {
    timeout: BOOTSTRAP_REQUEST_TIMEOUT_MS
  });
  return response.data;
}

export async function createOrganization(
  token: string,
  input: CreateOrganizationDto
) {
  const client = getCindroApiClient(token);
  const response = await client.post<CindroOrganization>(
    '/organizations',
    input
  );
  return response.data;
}

export async function listOrganizationApps(
  token: string,
  organizationId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.get<CindroApp[]>(
    `/organizations/${organizationId}/projects`,
    { timeout: BOOTSTRAP_REQUEST_TIMEOUT_MS }
  );
  return response.data;
}

export const listOrganizationProjects = listOrganizationApps;

export async function createOrganizationApp(
  token: string,
  organizationId: string,
  input: CreateAppDto
) {
  const client = getCindroApiClient(token);
  const response = await client.post<CindroApp>(
    `/organizations/${organizationId}/projects`,
    input
  );
  return response.data;
}

export const createOrganizationProject = createOrganizationApp;

export async function updateOrganizationProject(
  token: string,
  organizationId: string,
  projectId: string,
  input: { name?: string; slug?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.patch<CindroApp>(
    `/organizations/${organizationId}/projects/${projectId}`,
    input
  );
  return response.data;
}

export async function deleteOrganizationApp(
  token: string,
  organizationId: string,
  projectId: string
) {
  const client = getCindroApiClient(token);
  await client.delete(`/organizations/${organizationId}/projects/${projectId}`);
}

export const deleteOrganizationProject = deleteOrganizationApp;

export async function deleteOrganization(
  token: string,
  organizationId: string
) {
  const client = getCindroApiClient(token);
  await client.delete(`/organizations/${organizationId}`);
}

export async function listOrganizationAppEnvironments(
  token: string,
  organizationId: string,
  projectId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.get<EnvironmentRow[]>(
    `/organizations/${organizationId}/projects/${projectId}/environments`
  );
  return response.data;
}

export const listOrganizationProjectEnvironments =
  listOrganizationAppEnvironments;

export async function createOrganizationAppEnvironment(
  token: string,
  organizationId: string,
  projectId: string,
  input: CreateEnvironmentDto
) {
  const client = getCindroApiClient(token);
  const response = await client.post<EnvironmentRow>(
    `/organizations/${organizationId}/projects/${projectId}/environments`,
    input
  );
  return response.data;
}

export const createOrganizationProjectEnvironment =
  createOrganizationAppEnvironment;

export async function listOrganizationTeams(
  token: string,
  organizationId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.get<CindroTeam[]>(
    `/organizations/${organizationId}/teams`
  );
  return response.data;
}

export async function createOrganizationTeam(
  token: string,
  organizationId: string,
  input: CreateTeamDto
) {
  const client = getCindroApiClient(token);
  const response = await client.post<CindroTeam>(
    `/organizations/${organizationId}/teams`,
    input
  );
  return response.data;
}

export async function listReleaseArtifacts(
  token: string,
  scope?: { organizationId?: string; projectId?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<ReleaseArtifactRow[]>('/releases', {
    params: scope || undefined
  });
  return response.data;
}

export async function listReleaseChannels(
  token: string,
  scope?: { organizationId?: string; projectId?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<ReleaseChannelRow[]>('/releases/channels', {
    params: scope || undefined
  });
  return response.data;
}

export async function listReleasePromotions(
  token: string,
  scope?: { organizationId?: string; projectId?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<ReleasePromotionRow[]>(
    '/releases/promotions',
    {
      params: scope || undefined
    }
  );
  return response.data;
}

export async function listReleaseShipments(
  token: string,
  scope: { organizationId: string; projectId: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<ReleaseShipmentRow[]>(
    '/release-shipments',
    { params: scope }
  );
  return response.data;
}

export async function createReleaseShipment(
  token: string,
  input: CreateReleaseShipmentInput
) {
  const client = getCindroApiClient(token);
  const response = await client.post<ReleaseShipmentRow>(
    '/release-shipments',
    input
  );
  return response.data;
}

export async function releaseShipment(
  token: string,
  releaseShipmentId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.post<ReleaseShipmentRow>(
    `/release-shipments/${releaseShipmentId}/release`,
    { releaseShipmentId }
  );
  return response.data;
}

export async function listDeliveryCampaigns(
  token: string,
  scope: { organizationId: string; projectId?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<DeliveryCampaignRow[]>(
    '/delivery-campaigns',
    { params: scope }
  );
  return response.data;
}

export async function createDeliveryCampaign(
  token: string,
  input: CreateDeliveryCampaignInput
) {
  const client = getCindroApiClient(token);
  const response = await client.post<DeliveryCampaignRow>(
    '/delivery-campaigns',
    input
  );
  return response.data;
}

export async function startDeliveryCampaign(
  token: string,
  deliveryCampaignId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.post<DeliveryCampaignRow>(
    `/delivery-campaigns/${deliveryCampaignId}/start`,
    { deliveryCampaignId }
  );
  return response.data;
}

export async function cancelDeliveryCampaign(
  token: string,
  deliveryCampaignId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.post<DeliveryCampaignRow>(
    `/delivery-campaigns/${deliveryCampaignId}/cancel`,
    { deliveryCampaignId }
  );
  return response.data;
}

export async function retryDeliveryCampaignTarget(
  token: string,
  deliveryCampaignId: string,
  targetKey: string
) {
  const client = getCindroApiClient(token);
  const response = await client.post<DeliveryCampaignRow>(
    `/delivery-campaigns/${deliveryCampaignId}/targets/${targetKey}/retry`,
    { deliveryCampaignId, targetKey }
  );
  return response.data;
}

export async function approveDeliveryCampaignTarget(
  token: string,
  deliveryCampaignId: string,
  targetKey: string
) {
  const client = getCindroApiClient(token);
  const response = await client.post<DeliveryCampaignRow>(
    `/delivery-campaigns/${deliveryCampaignId}/targets/${targetKey}/approve`,
    { deliveryCampaignId, targetKey }
  );
  return response.data;
}

export async function preflightDeliveryCampaign(
  token: string,
  deliveryCampaignId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.post<DeliveryCampaignRow>(
    `/delivery-campaigns/${deliveryCampaignId}/preflight`,
    { deliveryCampaignId }
  );
  return response.data;
}

export async function listPipelineRuns(
  token: string,
  scope?: { organizationId?: string; projectId?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<PipelineRunRow[]>('/pipeline/runs', {
    params: scope || undefined
  });
  return response.data;
}

export async function listStageRuns(
  token: string,
  scope?: {
    organizationId?: string;
    projectId?: string;
    pipelineRunId?: string;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<StageRunRow[]>('/pipeline/stages', {
    params: scope || undefined
  });
  return response.data;
}

export async function listProjectRepoMappings(
  token: string,
  scope?: { organizationId?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<ProjectRepoMappingRow[]>(
    '/pipeline/repo-mappings',
    { params: scope || undefined }
  );
  return response.data;
}

export async function listWorkJourneys(
  token: string,
  scope?: { organizationId?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<WorkJourneyRow[]>(
    '/pipeline/work-journeys',
    { params: scope || undefined }
  );
  return response.data;
}

export async function createWorkItemRelationship(
  token: string,
  sourceWorkJourneyItemId: string,
  targetWorkJourneyItemId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.post(
    '/pipeline/work-items/' +
      encodeURIComponent(sourceWorkJourneyItemId) +
      '/relationships',
    { targetWorkJourneyItemId }
  );
  return response.data;
}

export async function deleteWorkItemRelationship(
  token: string,
  workJourneyItemRelationshipId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.delete(
    '/pipeline/work-item-relationships/' +
      encodeURIComponent(workJourneyItemRelationshipId)
  );
  return response.data;
}

export async function listDeliveryCandidates(
  token: string,
  scope?: { organizationId?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<DeliveryCandidateRow[]>(
    '/pipeline/delivery-candidates',
    { params: scope || undefined }
  );
  return response.data;
}

export async function listRuntimeObservers(
  token: string,
  organizationId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.get<RuntimeObserverConnectionRow[]>(
    '/pipeline/runtime-observers',
    { params: { organizationId } }
  );
  return response.data;
}

export async function listRuntimeObservations(
  token: string,
  scope: { organizationId: string; projectId?: string; environment?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<RuntimeObservationRow[]>(
    '/pipeline/runtime-observations',
    { params: scope }
  );
  return response.data;
}

export async function createRuntimeObserver(
  token: string,
  input: {
    organizationId: string;
    projectId: string;
    name: string;
    environment: string;
    namespace: string;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.post<IssuedRuntimeObserverConnectionRow>(
    '/pipeline/runtime-observers',
    input
  );
  return response.data;
}

export async function rotateRuntimeObserver(
  token: string,
  runtimeObserverConnectionId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.post<IssuedRuntimeObserverConnectionRow>(
    `/pipeline/runtime-observers/${runtimeObserverConnectionId}/rotate`
  );
  return response.data;
}

export async function revokeRuntimeObserver(
  token: string,
  runtimeObserverConnectionId: string
) {
  const client = getCindroApiClient(token);
  await client.delete(
    `/pipeline/runtime-observers/${runtimeObserverConnectionId}`
  );
}

export async function listPipelineDefinitions(
  token: string,
  organizationId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.get<PipelineDefinitionRow[]>(
    '/pipeline/definitions',
    { params: { organizationId } }
  );
  return response.data;
}

export async function discoverPipelineDefinition(
  token: string,
  input: {
    projectRepoMappingId: string;
    activate?: boolean;
    sourcePath?: string;
    sourceRef?: string;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.post<PipelineDefinitionRow>(
    '/pipeline/definitions/discover',
    input
  );
  return response.data;
}

export async function savePipelineDefinition(
  token: string,
  input: {
    organizationId: string;
    projectId?: string;
    sourceContent: string;
    activate?: boolean;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.post<PipelineDefinitionRow>(
    '/pipeline/definitions/custom',
    input
  );
  return response.data;
}

export async function recordJourneyEvidence(
  token: string,
  input: {
    workJourneyId: string;
    stageKey: string;
    status: 'success' | 'failed';
    note?: string;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.post(
    `/pipeline/work-journeys/${input.workJourneyId}/evidence`,
    input
  );
  return response.data;
}

export async function remediateWorkJourney(
  token: string,
  input: {
    workJourneyId: string;
    workJourneyItemId: string;
    action: 'rerun_failed_jobs';
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.post(
    `/pipeline/work-journeys/${input.workJourneyId}/remediate`,
    input
  );
  return response.data as {
    status: 'success';
    dispatchRunId: string;
    repo: string;
    runId: number;
    url: string;
  };
}

export async function createProjectRepoMapping(
  token: string,
  input: {
    provider: string;
    externalRepo: string;
    projectId?: string;
    projectIds?: string[];
    verificationToken?: string;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.post<ProjectRepoMappingRow>(
    '/pipeline/repo-mappings',
    input
  );
  return response.data;
}

export async function updateProjectRepoMapping(
  token: string,
  projectRepoMappingId: string,
  projectIds: string[]
) {
  const client = getCindroApiClient(token);
  const response = await client.patch<ProjectRepoMappingRow>(
    `/pipeline/repo-mappings/${projectRepoMappingId}`,
    { projectIds }
  );
  return response.data;
}

export async function deleteProjectRepoMapping(
  token: string,
  projectRepoMappingId: string
) {
  const client = getCindroApiClient(token);
  await client.delete(`/pipeline/repo-mappings/${projectRepoMappingId}`);
}

export async function syncProjectRepoMapping(
  token: string,
  projectRepoMappingId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.post<{
    repo: string;
    scanned: number;
    synced: number;
    definition?: {
      status: 'activated' | 'missing' | 'invalid';
      name?: string;
      sourceSha?: string;
      message?: string;
    };
  }>(`/pipeline/repo-mappings/${projectRepoMappingId}/sync`);
  return response.data;
}

export async function startGithubRepoLinkOauth(
  token: string,
  externalRepo: string
) {
  const client = getCindroApiClient(token);
  const response = await client.get<{ authorizeUrl: string }>(
    '/pipeline/github/repo-link/start',
    { params: { externalRepo } }
  );
  return response.data;
}

export type ClickupConnectionRow = {
  platformConfigured: boolean;
  status:
    | 'not_connected'
    | 'configuration_required'
    | 'active'
    | 'error'
    | 'disabled';
  displayName?: string;
  availableWorkspaces: Array<{ id: string; name: string }>;
  workspaceId?: string;
  spaceIds: string[];
  availableSpaces: Array<{ id: string; name: string }>;
  lifecycle: {
    discoveredLists: ClickupListRow[];
    mappings: ClickupListMappingRow[];
    writeBackEnabled: boolean;
    writeBackRules: Record<string, boolean>;
    discoveredAt?: string;
    reconciliation?: {
      startedAt: string;
      completedAt: string;
      windowDays: number;
      listCount: number;
      scanned: number;
      synced: number;
    };
  };
  lastWebhookAt?: string;
  lastSyncAt?: string;
  errorMessage?: string;
};

export type ClickupLifecycleState =
  | 'planned'
  | 'ready'
  | 'active'
  | 'blocked'
  | 'validation'
  | 'delivered'
  | 'cancelled';
export type ClickupListRow = {
  id: string;
  name: string;
  spaceId: string;
  spaceName: string;
  folderName?: string;
  statuses: Array<{
    status: string;
    type?: string;
    color?: string;
    orderindex?: number | string;
  }>;
};
export type ClickupListMappingRow = {
  listId: string;
  listName: string;
  enabled?: boolean;
  statuses: Record<string, ClickupLifecycleState>;
  destinations: Partial<Record<ClickupLifecycleState, string>>;
};

export async function getClickupConnection(
  token: string,
  organizationId?: string
) {
  const response = await getCindroApiClient(token).get<ClickupConnectionRow>(
    '/pipeline/clickup/connection',
    { params: organizationId ? { organizationId } : undefined }
  );
  return response.data;
}

export async function startClickupOauth(
  token: string,
  returnPath: string,
  organizationId?: string
) {
  const response = await getCindroApiClient(token).get<{
    authorizeUrl: string;
  }>('/pipeline/clickup/oauth/start', {
    params: { returnPath, ...(organizationId ? { organizationId } : {}) }
  });
  return response.data;
}

export async function configureClickupConnection(
  token: string,
  workspaceId: string,
  spaceIds: string[],
  organizationId?: string
) {
  const response = await getCindroApiClient(token).post<ClickupConnectionRow>(
    '/pipeline/clickup/connection',
    { ...(organizationId ? { organizationId } : {}), workspaceId, spaceIds }
  );
  return response.data;
}

export async function discoverClickupLifecycle(
  token: string,
  organizationId?: string
) {
  const response = await getCindroApiClient(token).post<ClickupConnectionRow>(
    '/pipeline/clickup/lifecycle/discover',
    organizationId ? { organizationId } : {}
  );
  return response.data;
}

export async function saveClickupLifecycle(
  token: string,
  input: {
    mappings: ClickupListMappingRow[];
    writeBackEnabled: boolean;
    writeBackRules: Record<string, boolean>;
  },
  organizationId?: string
) {
  const response = await getCindroApiClient(token).post<ClickupConnectionRow>(
    '/pipeline/clickup/lifecycle',
    { ...(organizationId ? { organizationId } : {}), ...input }
  );
  return response.data;
}

export async function reconcileClickup(token: string, organizationId?: string) {
  const response = await getCindroApiClient(token).post<
    NonNullable<ClickupConnectionRow['lifecycle']['reconciliation']>
  >('/pipeline/clickup/reconcile', organizationId ? { organizationId } : {});
  return response.data;
}

export async function disconnectClickupConnection(
  token: string,
  organizationId?: string
) {
  await getCindroApiClient(token).delete('/pipeline/clickup/connection', {
    params: organizationId ? { organizationId } : undefined
  });
}

export type PlaneConnectionRow = {
  platformConfigured: boolean;
  webhookUrl?: string;
  status: 'not_connected' | 'active' | 'error' | 'disabled';
  displayName?: string;
  instanceUrl?: string;
  workspaceSlug?: string;
  projects: Array<{ id: string; identifier: string }>;
  lastWebhookAt?: string;
  errorMessage?: string;
  mode: 'webhook_read_only';
};

export async function getPlaneConnection(
  token: string,
  organizationId?: string
) {
  const response = await getCindroApiClient(token).get<PlaneConnectionRow>(
    '/pipeline/plane/connection',
    { params: organizationId ? { organizationId } : undefined }
  );
  return response.data;
}

export async function configurePlaneConnection(
  token: string,
  input: {
    organizationId?: string;
    instanceUrl: string;
    workspaceSlug: string;
    projects: Array<{ id: string; identifier: string }>;
    webhookSecret?: string;
  }
) {
  const response = await getCindroApiClient(token).post<PlaneConnectionRow>(
    '/pipeline/plane/connection',
    input
  );
  return response.data;
}

export async function disconnectPlaneConnection(
  token: string,
  organizationId?: string
) {
  const response = await getCindroApiClient(token).delete<{
    disconnected: boolean;
    removeWebhookInPlane: boolean;
  }>('/pipeline/plane/connection', {
    params: organizationId ? { organizationId } : undefined
  });
  return response.data;
}

export async function getAgentRunners(token: string, organizationId: string) {
  const response = await getCindroApiClient(token).get<AgentRunnerRow[]>(
    '/pipeline/agent-runners',
    { params: { organizationId } }
  );
  return response.data;
}

export async function getAgentProfiles(token: string, organizationId: string) {
  const response = await getCindroApiClient(token).get<AgentProfileRow[]>(
    '/pipeline/agent-profiles',
    { params: { organizationId } }
  );
  return response.data;
}

export async function saveAgentProfile(
  token: string,
  input: {
    organizationId: string;
    agentProfileId?: string;
    name: string;
    role: AgentProfileRow['role'];
    instructions: string;
    modelProvider: AgentProfileRow['modelProvider'];
    model: string;
    taskProviders: string[];
    apiKey?: string;
    status?: AgentProfileRow['status'];
  }
) {
  const response = await getCindroApiClient(token).post<AgentProfileRow>(
    '/pipeline/agent-profiles',
    input
  );
  return response.data;
}

export async function getAgentWorkPlans(token: string, organizationId: string) {
  const response = await getCindroApiClient(token).get<AgentWorkPlanRow[]>(
    '/pipeline/agent-work-plans',
    { params: { organizationId } }
  );
  return response.data;
}

export async function startAgentWorkPlan(
  token: string,
  input: {
    organizationId: string;
    agentProfileId: string;
    taskItemIds: string[];
    additionalContext?: string;
  }
) {
  const response = await getCindroApiClient(token).post<AgentWorkPlanRow>(
    '/pipeline/agent-work-plans',
    input
  );
  return response.data;
}

export async function updateAgentWorkPlan(
  token: string,
  agentWorkPlanId: string,
  status: 'accepted' | 'stopped'
) {
  const response = await getCindroApiClient(token).patch<AgentWorkPlanRow>(
    `/pipeline/agent-work-plans/${encodeURIComponent(agentWorkPlanId)}`,
    { status }
  );
  return response.data;
}

export async function setAgentRunnerRepositoryAccess(
  token: string,
  agentRunnerId: string,
  connectorResourceMappingIds: string[]
) {
  const response = await getCindroApiClient(token).patch<{
    agentRunnerId: string;
    allowedConnectorResourceMappingIds: string[];
  }>(`/pipeline/agent-runners/${agentRunnerId}/repositories`, {
    connectorResourceMappingIds
  });
  return response.data;
}

export async function createAgentRunner(
  token: string,
  organizationId: string,
  name: string
) {
  const response = await getCindroApiClient(token).post<AgentRunnerRow>(
    '/pipeline/agent-runners',
    { organizationId, name }
  );
  return response.data;
}

export async function rotateAgentRunner(token: string, agentRunnerId: string) {
  const response = await getCindroApiClient(token).post<AgentRunnerRow>(
    `/pipeline/agent-runners/${agentRunnerId}/rotate`,
    {}
  );
  return response.data;
}

export async function revokeAgentRunner(token: string, agentRunnerId: string) {
  const response = await getCindroApiClient(token).delete<{ revoked: boolean }>(
    `/pipeline/agent-runners/${agentRunnerId}`
  );
  return response.data;
}

export async function startAgentRunnerRepositoryCheck(
  token: string,
  input: {
    organizationId: string;
    agentRunnerId: string;
    connectorResourceMappingId: string;
  }
) {
  const response = await getCindroApiClient(token).post<AgentRunnerJobRow>(
    '/pipeline/agent-runner-jobs/repository-check',
    input
  );
  return response.data;
}

export async function getAgentRunnerJobs(
  token: string,
  organizationId: string
) {
  const response = await getCindroApiClient(token).get<AgentRunnerJobRow[]>(
    '/pipeline/agent-runner-jobs',
    { params: { organizationId } }
  );
  return response.data;
}

export async function retryAgentRunnerJob(
  token: string,
  agentRunnerJobId: string,
  agentRunnerId: string
) {
  const response = await getCindroApiClient(token).post<AgentRunnerJobRow>(
    `/pipeline/agent-runner-jobs/${agentRunnerJobId}/retry`,
    { agentRunnerId }
  );
  return response.data;
}

export async function cancelAgentRunnerJob(
  token: string,
  agentRunnerJobId: string
) {
  const response = await getCindroApiClient(token).post<AgentRunnerJobRow>(
    `/pipeline/agent-runner-jobs/${agentRunnerJobId}/cancel`,
    {}
  );
  return response.data;
}

export async function dispatchPipelineStage(
  token: string,
  input: {
    connectorType?: string;
    connectorAction?: string;
    actionConfig?: Record<string, unknown>;
    pipelineRunId?: string;
    workJourneyId?: string;
    pipelineDefinitionId?: string;
    stageKey?: string;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.post<DispatchRunRow>(
    '/pipeline/dispatch',
    input
  );
  return response.data;
}

export async function triageWorkJourneyAttention(
  token: string,
  input: {
    workJourneyAttentionId: string;
    action:
      | 'assign_self'
      | 'assign_user'
      | 'acknowledge'
      | 'snooze'
      | 'resolve';
    assigneeUserId?: string;
    snoozeMinutes?: number;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.post(
    `/pipeline/work-journey-attention/${input.workJourneyAttentionId}/triage`,
    input
  );
  return response.data;
}

export async function claimWorkIdentity(token: string, workIdentityId: string) {
  const response = await getCindroApiClient(token).post<{
    workIdentityId: string;
    tenantUserId: string;
    linkSource: 'manual';
  }>(`/pipeline/work-identities/${workIdentityId}/claim`, { workIdentityId });
  return response.data;
}

export async function listCindroApiKeys(token: string) {
  const client = getCindroApiClient(token);
  const response = await client.get<CindroApiKeyRow[]>('/auth/api-keys');
  return response.data;
}

export const listTenantApiKeys = listCindroApiKeys;

export async function createCindroApiKey(
  token: string,
  input: { name: string; scopes: string[]; expiresAt?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.post<{
    tenantApiKeyId: string;
    name: string;
    scopes: string[];
    rawKey: string;
    prefix: string;
  }>('/auth/api-keys', input);
  return response.data;
}

export const createTenantApiKey = createCindroApiKey;

export async function deleteCindroApiKey(
  token: string,
  tenantApiKeyId: string
) {
  const client = getCindroApiClient(token);
  await client.delete(`/auth/api-keys/${tenantApiKeyId}`);
}

export const deleteTenantApiKey = deleteCindroApiKey;

export async function listRuntimeConfigs(
  token: string,
  scope?: {
    organizationId?: string;
    projectId?: string;
    tenantEnvironmentId?: string;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<RuntimeConfigRow[]>('/runtime/configs', {
    params: scope || undefined
  });
  return response.data;
}

export async function createRuntimeConfig(
  token: string,
  input: RuntimeConfigInput
) {
  const client = getCindroApiClient(token);
  const response = await client.post<RuntimeConfigRow>(
    '/runtime/configs',
    input
  );
  return response.data;
}

export async function updateRuntimeConfig(
  token: string,
  runtimeConfigId: string,
  input: Partial<RuntimeConfigInput>
) {
  const client = getCindroApiClient(token);
  const response = await client.patch<RuntimeConfigRow>(
    `/runtime/configs/${runtimeConfigId}`,
    { runtimeConfigId, ...input }
  );
  return response.data;
}

export async function createRuntimeConfigEntry(
  token: string,
  runtimeConfigId: string,
  input: RuntimeConfigValueInput
) {
  const client = getCindroApiClient(token);
  const response = await client.post<RuntimeConfigValueRow>(
    `/runtime/configs/${runtimeConfigId}/entries`,
    { runtimeConfigId, ...input }
  );
  return response.data;
}

export async function updateRuntimeConfigEntry(
  token: string,
  runtimeConfigId: string,
  entryId: string,
  input: Partial<RuntimeConfigValueInput>
) {
  const client = getCindroApiClient(token);
  const response = await client.patch<RuntimeConfigValueRow>(
    `/runtime/configs/${runtimeConfigId}/entries/${entryId}`,
    { runtimeConfigId, entryId, ...input }
  );
  return response.data;
}

export async function deleteRuntimeConfigEntry(
  token: string,
  runtimeConfigId: string,
  entryId: string
) {
  const client = getCindroApiClient(token);
  // Both DTO fields are populated from the path segments below — same
  // path-param merge the framework already does for PATCH/DELETE on the
  // parent rule endpoint. No body needed (some proxies strip DELETE bodies).
  await client.delete(`/runtime/configs/${runtimeConfigId}/entries/${entryId}`);
}

export async function getReleaseStorageConfig(token: string) {
  const client = getCindroApiClient(token);
  const response = await client.get<ReleaseStorageConfigRow>(
    '/runtime/storage-config'
  );
  return response.data;
}

export async function saveReleaseStorageConfig(
  token: string,
  input: Partial<ReleaseStorageConfigRow> & {
    storageSecretAccessKey?: string;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.put<ReleaseStorageConfigRow>(
    '/runtime/storage-config',
    input
  );
  return response.data;
}

export async function createReleaseChannel(
  token: string,
  input: CreateReleaseChannelDto
) {
  const client = getCindroApiClient(token);
  const response = await client.post<ReleaseChannelRow>(
    '/releases/channels',
    input
  );
  return response.data;
}

export async function updateReleaseChannel(
  token: string,
  environment: string,
  channel: string,
  input: UpdateReleaseChannelDto
) {
  const client = getCindroApiClient(token);
  const response = await client.patch<ReleaseChannelRow>(
    `/releases/channels/${environment}/${channel}`,
    input
  );
  return response.data;
}

export async function promoteReleaseArtifact(
  token: string,
  releaseArtifactId: string,
  input: PromoteReleaseDto
) {
  const client = getCindroApiClient(token);
  const response = await client.post<ReleasePromotionRow>(
    `/releases/${releaseArtifactId}/promote`,
    input
  );
  return response.data;
}

export async function rollbackReleaseChannel(
  token: string,
  environment: string,
  channel: string,
  input: RollbackReleaseDto
) {
  const client = getCindroApiClient(token);
  const response = await client.post<ReleasePromotionRow>(
    `/releases/${environment}/${channel}/rollback`,
    input
  );
  return response.data;
}

// --- Org member management ---

export async function listOrganizationMembers(
  token: string,
  organizationId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.get<CindroOrgMember[]>(
    `/organizations/${organizationId}/members`
  );
  return response.data;
}

export async function updateOrganizationMemberRole(
  token: string,
  organizationId: string,
  memberId: string,
  role: 'admin' | 'member' | 'viewer'
) {
  const client = getCindroApiClient(token);
  const response = await client.patch<CindroOrgMember>(
    `/organizations/${organizationId}/members/${memberId}`,
    { role }
  );
  return response.data;
}

export async function removeOrganizationMember(
  token: string,
  organizationId: string,
  memberId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.delete<{ ok: true }>(
    `/organizations/${organizationId}/members/${memberId}`
  );
  return response.data;
}

export async function createOrganizationInvite(
  token: string,
  organizationId: string,
  input: { role: 'admin' | 'member' | 'viewer'; emailHint?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.post<CindroOrgInvite>(
    `/organizations/${organizationId}/invites`,
    input
  );
  return response.data;
}

export async function listOrganizationInvites(
  token: string,
  organizationId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.get<CindroOrgInvite[]>(
    `/organizations/${organizationId}/invites`
  );
  return response.data;
}

export async function revokeOrganizationInvite(
  token: string,
  organizationId: string,
  inviteId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.delete<{ ok: true }>(
    `/organizations/${organizationId}/invites/${inviteId}`
  );
  return response.data;
}

export async function acceptOrganizationInvite(
  token: string,
  rawToken: string
) {
  const client = getCindroApiClient(token);
  const response = await client.post<{ organizationId: string; role: string }>(
    `/invites/${rawToken}/accept`
  );
  return response.data;
}

// ─── Super-Admin API ───────────────────────────────────────────────────────

export type SystemConfigRow = {
  key: string;
  value: string;
  lastModifiedBy?: string;
};

export type AdminUserRow = {
  tenantUserId: string;
  email: string;
  displayName: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  lastLoginAt?: string;
};

export type AdminOrganizationRow = {
  tenantOrganizationId: string;
  slug: string;
  name: string;
  region: string;
  dataPlaneMode: string;
  isActive: boolean;
  createdAt?: string;
};

export async function getAdminSystemConfig(token: string) {
  const client = getCindroApiClient(token);
  const response = await client.get<SystemConfigRow[]>('/admin/config');
  return response.data;
}

export async function updateAdminSystemConfig(
  token: string,
  key: string,
  value: string
) {
  const client = getCindroApiClient(token);
  const response = await client.put<SystemConfigRow>(`/admin/config/${key}`, {
    key,
    value
  });
  return response.data;
}

export async function listAdminUsers(token: string) {
  const client = getCindroApiClient(token);
  const response = await client.get<AdminUserRow[]>('/admin/users');
  return response.data;
}

export async function patchAdminUser(
  token: string,
  userId: string,
  patch: { isActive?: boolean; isSuperAdmin?: boolean }
) {
  const client = getCindroApiClient(token);
  const response = await client.patch<AdminUserRow>(`/admin/users/${userId}`, {
    userId,
    ...patch
  });
  return response.data;
}

export async function listAdminOrganizations(token: string) {
  const client = getCindroApiClient(token);
  const response = await client.get<AdminOrganizationRow[]>(
    '/admin/organizations'
  );
  return response.data;
}

export async function patchAdminOrganization(
  token: string,
  organizationId: string,
  patch: { isActive?: boolean }
) {
  const client = getCindroApiClient(token);
  const response = await client.patch<AdminOrganizationRow>(
    `/admin/organizations/${organizationId}`,
    { organizationId, ...patch }
  );
  return response.data;
}

export type AuditEventRow = {
  auditEventId: string;
  tenantUserId?: string | null;
  email?: string | null;
  organizationId?: string | null;
  action: string;
  description?: string | null;
  method: string;
  path: string;
  status: number;
  createdAt: string;
};

export async function listAuditEvents(token: string, organizationId: string) {
  const client = getCindroApiClient(token);
  const response = await client.get<AuditEventRow[]>('/audit-events', {
    params: { organizationId }
  });
  return response.data;
}

// ── Ingestion (Alert Sources) ────────────────────────────────────────────────

export type IngestionSourceKind = 'generic' | 'grafana';
export type IngestionTargetEvidenceType =
  | 'alert'
  | 'work_item'
  | 'feedback'
  | 'generic';

export type IngestionFieldMappingRule = {
  targetField: string;
  sourcePath: string;
};

export type IngestionSourceRow = {
  ingestionSourceId: string;
  organizationId: string;
  projectId?: string;
  name: string;
  targetEvidenceType: IngestionTargetEvidenceType;
  sourceKind: IngestionSourceKind;
  fieldMapping: IngestionFieldMappingRule[];
  webhookToken: string;
  webhookPath: string;
  status: 'active' | 'paused';
  created: string;
  updated: string;
};

export type CreateIngestionSourceInput = {
  organizationId: string;
  projectId?: string;
  name: string;
  targetEvidenceType: IngestionTargetEvidenceType;
  sourceKind?: IngestionSourceKind;
  fieldMapping?: IngestionFieldMappingRule[];
};

// The create response additionally carries the raw shared secret — for a
// `grafana` source this is the bearer token, for `generic` it's the HMAC
// signing secret. Returned exactly once, the same reveal-once pattern as a
// tenant API key: only a hash is ever stored server-side.
export type CreateIngestionSourceResult = IngestionSourceRow & {
  secret: string;
};

export async function listIngestionSources(
  token: string,
  scope: { organizationId: string; projectId?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<{ sources: IngestionSourceRow[] }>(
    '/ingestion/sources',
    { params: scope }
  );
  return response.data.sources;
}

export async function createIngestionSource(
  token: string,
  input: CreateIngestionSourceInput
) {
  const client = getCindroApiClient(token);
  const response = await client.post<CreateIngestionSourceResult>(
    '/ingestion/sources',
    input
  );
  return response.data;
}

// ── On-Call: schedules & shifts ──────────────────────────────────────────────

export type OnCallRotationType = 'daily' | 'weekly' | 'custom';

export type OnCallScheduleRow = {
  onCallScheduleId: string;
  organizationId: string;
  teamHandle: string;
  name: string;
  rotationType: OnCallRotationType;
  timezone: string;
  created: string;
  updated: string;
};

export async function listOnCallSchedules(
  token: string,
  scope: { organizationId: string; teamHandle?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<{ schedules: OnCallScheduleRow[] }>(
    '/oncall/schedules',
    { params: scope }
  );
  return response.data.schedules;
}

export async function createOnCallSchedule(
  token: string,
  input: {
    organizationId: string;
    teamHandle: string;
    name: string;
    rotationType: OnCallRotationType;
    timezone: string;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.post<OnCallScheduleRow>(
    '/oncall/schedules',
    input
  );
  return response.data;
}

export type OnCallShiftRow = {
  onCallShiftId: string;
  onCallScheduleId: string;
  workIdentityId: string;
  startsAt: string;
  endsAt: string;
  isOverride: boolean;
  created: string;
  updated: string;
};

export async function listOnCallShifts(
  token: string,
  onCallScheduleId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.get<{ shifts: OnCallShiftRow[] }>(
    '/oncall/shifts',
    { params: { onCallScheduleId } }
  );
  return response.data.shifts;
}

export async function createOnCallShift(
  token: string,
  input: {
    onCallScheduleId: string;
    workIdentityId: string;
    startsAt: string;
    endsAt: string;
    isOverride?: boolean;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.post<OnCallShiftRow>('/oncall/shifts', input);
  return response.data;
}

// ── On-Call: escalation policies & steps ─────────────────────────────────────

export type EscalationPolicyRow = {
  escalationPolicyId: string;
  organizationId: string;
  teamHandle: string;
  appliesToCategory?: string;
  created: string;
  updated: string;
};

export async function listEscalationPolicies(
  token: string,
  scope: { organizationId: string; teamHandle?: string }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<{ policies: EscalationPolicyRow[] }>(
    '/oncall/escalation-policies',
    { params: scope }
  );
  return response.data.policies;
}

export async function createEscalationPolicy(
  token: string,
  input: {
    organizationId: string;
    teamHandle: string;
    appliesToCategory?: string;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.post<EscalationPolicyRow>(
    '/oncall/escalation-policies',
    input
  );
  return response.data;
}

export type EscalationChannel = 'in_app' | 'email';
export type EscalationStepTargetType = 'on_call' | 'specific_identity';

export type EscalationStepRow = {
  escalationStepId: string;
  escalationPolicyId: string;
  stepOrder: number;
  channel: EscalationChannel;
  timeoutSeconds: number;
  targetType: EscalationStepTargetType;
  targetWorkIdentityId?: string;
  created: string;
  updated: string;
};

export async function listEscalationSteps(
  token: string,
  escalationPolicyId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.get<{ steps: EscalationStepRow[] }>(
    '/oncall/escalation-steps',
    { params: { escalationPolicyId } }
  );
  return response.data.steps;
}

export async function createEscalationStep(
  token: string,
  input: {
    escalationPolicyId: string;
    stepOrder: number;
    channel: EscalationChannel;
    timeoutSeconds: number;
    targetType: EscalationStepTargetType;
    targetWorkIdentityId?: string;
  }
) {
  const client = getCindroApiClient(token);
  const response = await client.post<EscalationStepRow>(
    '/oncall/escalation-steps',
    input
  );
  return response.data;
}

// ── On-Call: escalation runs ──────────────────────────────────────────────────

export type EscalationRunStatus =
  | 'pending'
  | 'acknowledged'
  | 'resolved'
  | 'exhausted';
export type EscalationDispatchStatus = 'pending' | 'sent' | 'failed';

export type EscalationDispatchRow = {
  escalationDispatchId: string;
  escalationStepId?: string;
  channel: EscalationChannel;
  status: EscalationDispatchStatus;
  providerResponse?: Record<string, unknown>;
  sentAt?: string;
  created: string;
};

export type EscalationRunRow = {
  escalationRunId: string;
  organizationId: string;
  escalationPolicyId?: string;
  fingerprint: string;
  status: EscalationRunStatus;
  severity: string;
  title: string;
  description?: string;
  sourceUrl?: string;
  labels: Record<string, string>;
  startedAt: string;
  acknowledgedAt?: string;
  acknowledgedByWorkIdentityId?: string;
  resolvedAt?: string;
  dispatches: EscalationDispatchRow[];
  created: string;
  updated: string;
};

export async function listEscalationRuns(
  token: string,
  scope: { organizationId: string; limit?: number }
) {
  const client = getCindroApiClient(token);
  const response = await client.get<{ runs: EscalationRunRow[] }>(
    '/oncall/escalation-runs',
    { params: scope }
  );
  return response.data.runs;
}

export async function acknowledgeEscalationRun(
  token: string,
  escalationRunId: string
) {
  const client = getCindroApiClient(token);
  const response = await client.post<EscalationRunRow>(
    `/oncall/escalation-runs/${escalationRunId}/acknowledge`,
    { escalationRunId }
  );
  return response.data;
}

export async function changeAccountPassword(
  session: CindroSession,
  currentPassword: string,
  newPassword: string
): Promise<string> {
  const client = getCindroApiClient(session.accessToken);
  const response = await client.post<{
    accessToken: string;
    refreshToken: string;
    message: string;
  }>('/auth/change-password', { currentPassword, newPassword });
  const next: CindroSession = {
    accessToken: response.data.accessToken,
    refreshToken: response.data.refreshToken,
    user: session.user
  };
  setSessionTokens(next);
  emitSessionChange(next);
  return response.data.message;
}

export async function forgotPassword(
  email: string
): Promise<{ message: string }> {
  const client = getCindroApiClient();
  const response = await client.post<{ message: string }>(
    '/auth/forgot-password',
    { email }
  );
  return response.data;
}

export async function resetPassword(
  token: string,
  password: string
): Promise<{ message: string }> {
  const client = getCindroApiClient();
  const response = await client.post<{ message: string }>(
    '/auth/reset-password',
    { token, password }
  );
  return response.data;
}
